/**
 * The on-device gate that decides whether a video frame is worth an API call.
 *
 * This is what keeps a live camera from costing a fortune and, more
 * importantly, from feeding blurry frames to a model that will confidently
 * misread them. A wrong date is worse than no date (AGENTS.md rule 1), and a
 * motion-blurred `2028` reads as `2026` — the exact Hajmola failure.
 *
 * Pure functions over a grayscale buffer so the thresholds are testable without
 * a camera. Ported from `prototype/public/app.js`, where the numbers were tuned
 * against real packaging on a real phone.
 */
/** Long edge sent to the model. NEVER lower this — see ADR 0004. */
export const MAX_EDGE = 1568;
export const JPEG_QUALITY = 0.72;
/** How often a frame is examined. Cheap; this is local work only. */
export const TICK_MS = 220;
/** Floor on the gap between API calls, regardless of what the gate says. */
export const COOLDOWN_MS = 1100;
/** Laplacian-variance floor. Below this the frame is too soft to read. */
export const SHARP_MIN = 42;
/** Mean absolute difference from the last SENT frame. Stops re-sending stills. */
export const DIFF_MIN = 6;
/** How much the scene must change before a settled item starts a new one. */
export const RESUME_DIFF = 30;
/** Consecutive empty scans before the sweep pauses itself. */
export const IDLE_LIMIT = 3;
/** Confidence floors for accepting a field into the record. */
export const ACCEPT_NAME = 0.55;
export const ACCEPT_DATE = 0.55;
/** RGBA (as from `getImageData`) to single-channel luma. */
export function toGray(rgba, width, height) {
    const g = new Uint8Array(width * height);
    for (let i = 0, p = 0; i < rgba.length; i += 4, p++) {
        g[p] =
            ((rgba[i] ?? 0) * 0.299 + (rgba[i + 1] ?? 0) * 0.587 + (rgba[i + 2] ?? 0) * 0.114) |
                0;
    }
    return { data: g, width, height };
}
/**
 * Variance of the Laplacian — the standard cheap blur metric.
 *
 * High on a crisp edge, near zero on a smooth gradient. Returns the standard
 * deviation rather than the variance so the threshold stays in a human range.
 */
export function sharpness({ data, width, height }) {
    let sum = 0;
    let sumSq = 0;
    let n = 0;
    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            const i = y * width + x;
            const v = 4 * (data[i] ?? 0) -
                (data[i - 1] ?? 0) -
                (data[i + 1] ?? 0) -
                (data[i - width] ?? 0) -
                (data[i + width] ?? 0);
            sum += v;
            sumSq += v * v;
            n++;
        }
    }
    if (n === 0)
        return 0;
    const mean = sum / n;
    return Math.sqrt(Math.max(0, sumSq / n - mean * mean));
}
/** Mean absolute difference. 255 when there is nothing to compare against. */
export function difference(g, prev) {
    if (!prev || prev.data.length !== g.data.length)
        return 255;
    let s = 0;
    for (let i = 0; i < g.data.length; i++) {
        s += Math.abs((g.data[i] ?? 0) - (prev.data[i] ?? 0));
    }
    return s / g.data.length;
}
/**
 * Should this frame be sent?
 *
 * Order matters. Cheap state checks come before the pixel maths, and the
 * `settled` check comes before everything else: once an item is complete the
 * sweep must STOP until the scene genuinely changes. Without it the motion gate
 * sees a reset baseline and re-fires on the same static packet forever — a real
 * bug from the prototype, reported as "does it never stop or something".
 */
export function shouldSend(input) {
    const { frame, lastSent, settledOn, msSinceLastCall, inFlight, forced } = input;
    if (inFlight)
        return { send: false, reason: "in_flight" };
    if (forced)
        return { send: true, reason: "forced" };
    if (msSinceLastCall < COOLDOWN_MS)
        return { send: false, reason: "cooldown" };
    if (settledOn) {
        return difference(frame, settledOn) >= RESUME_DIFF
            ? { send: true, reason: "new_scene" }
            : { send: false, reason: "settled" };
    }
    if (sharpness(frame) < SHARP_MIN)
        return { send: false, reason: "blurry" };
    if (difference(frame, lastSent) < DIFF_MIN)
        return { send: false, reason: "static" };
    return { send: true, reason: "changed" };
}
//# sourceMappingURL=gate.js.map